import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { request } from '@/lib/api';
import type { PlanKey } from '@/lib/plan-catalog';

import { billingSummarySchema, businessBillingSchema } from './admin-schemas';

/*
  מנויים בקונסולת המפעיל: סיכום (MRR/ARR, ספירות, התפלגות) ומנוי של עסק אחד.
  כל קריאה וכל כתיבה נרשמות ביומן הפלטפורמה בשרת — אין רענון אוטומטי.
*/

const ROOT = ['platform-admin'] as const;
const base = { staleTime: 60_000, refetchOnWindowFocus: false } as const;

export function useBillingSummary(enabled = true) {
  return useQuery({
    queryKey: [...ROOT, 'billing'],
    queryFn: ({ signal }) =>
      request('/platform-admin/billing', { schema: billingSummarySchema, signal }),
    enabled,
    ...base,
  });
}

export function useBusinessBilling(id: string | null) {
  return useQuery({
    queryKey: [...ROOT, 'business-billing', id],
    queryFn: ({ signal }) =>
      request(`/platform-admin/businesses/${encodeURIComponent(id!)}/billing`, {
        schema: businessBillingSchema,
        signal,
      }),
    enabled: id !== null,
    ...base,
  });
}

export type BillingAction =
  | { kind: 'extend-trial'; days: number }
  | { kind: 'coupon'; percent: number; months: number }
  | { kind: 'remove-coupon' }
  | { kind: 'complimentary'; plan: PlanKey; until?: string; extraSeats?: number };

function pathAndBody(a: BillingAction): [string, object] {
  switch (a.kind) {
    case 'extend-trial':
      return ['extend-trial', { days: a.days }];
    case 'coupon':
      return ['coupon', { percent: a.percent, months: a.months }];
    case 'remove-coupon':
      return ['coupon/remove', {}];
    case 'complimentary': {
      const body: { plan: PlanKey; until?: string; extraSeats?: number } = { plan: a.plan };
      if (a.until) body.until = a.until;
      if (a.extraSeats !== undefined) body.extraSeats = a.extraSeats;
      return ['complimentary', body];
    }
  }
}

/** אחרי כתיבה: מגירת העסק, סיכום המנויים והיומן — כל מה שתחת platform-admin. */
export function useBillingAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: BillingAction }) => {
      const [path, body] = pathAndBody(action);
      return request(`/platform-admin/businesses/${encodeURIComponent(id)}/billing/${path}`, {
        method: 'POST',
        body,
        schema: businessBillingSchema,
      });
    },
    onSuccess: (data, { id }) => {
      qc.setQueryData([...ROOT, 'business-billing', id], data);
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}
